import { Switch } from "@/components/ui";
import { routes } from "@/routes/utils";
import moment from "moment";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetSchoolsQuery } from "@/services";
import { useAppDispatch } from "@/redux/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import useToogleSchoolMutation from "@/services/school/useToogleSchoolMutation";

const useSchool = () => {
  const navigate = useNavigate();
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  // Define Table Columns
  const columns = [
    {
      accessorKey: "schoolName",
      header: <div>School Name</div>,
      cell: (row: any) => (
        <span
          className="text-primary font-normal cursor-pointer"
          onClick={() => onRowClick(row.row.original)}
        >
          {String(row?.row?.original?.schoolName || "-")}
        </span>
      ),
      size: 80,
    },
    {
      accessorKey: "contactNo",
      header: <div>Contact No.</div>,
      accessorFn: (row: any) => String(row?.phoneNumber || ""),
    },
    {
      accessorKey: "state",
      header: <div>State</div>,
      accessorFn: (row: any) => String(row?.state || ""),
    },
    {
      accessorKey: "city",
      header: <div>City</div>,
      accessorFn: (row: any) => String(row?.city || ""),
    },
    {
      accessorKey: "createdAt",
      header: <div>Onboarding Date</div>,
      accessorFn: (row: any) =>
        row?.createdAt ? moment(row?.createdAt).format("DD-MM-YYYY") : "",
    },
    {
      accessorKey: "action",
      header: <div>Action</div>,
      cell: (row: any) => (
        <Switch
          checked={row.row.original.status === "ACTIVE"}
          className="cursor-pointer"
          onCheckedChange={() =>
            toggleSchool({ id: row.row.original.schoolId })
          }
        />
      ),
    },
  ];

  const onAdd = () => navigate(`${routes.add}`);
  const onRowClick = (row: any) => navigate(`${row.schoolId}/${routes.edit}`);
  const onCancelFilter = () => setOpenFilter(false);
  const onFilterBtnClick = () => setOpenFilter(true);

  
  const params = useMemo(() => {
    const baseParams = {
      page: pageNumber + 1,
      limit: pageSize,
      orderBy: "createdAt",
      orderDirection: "DESC",
    };

    const cleanFilters =
      filterData && typeof filterData === "object"
        ? Object.fromEntries(
            Object.entries(filterData).filter(
              ([, v]) => v !== null && v !== "" && v !== undefined
            )
          )
        : {};

    return { ...baseParams, ...cleanFilters };
  }, [pageNumber, pageSize, filterData]);

  // ✅ Mutation for toggle active/inactive
  const { mutate: toggleSchool } = useToogleSchoolMutation({
    onSuccess: () => {
      toast.success("School Status Updated Successfully");
      queryClient.invalidateQueries({ queryKey: ["schools"] });
    },
    onError: () => {
      toast.error("Failed to update status");
    },
  });
  const { data, isPending, isError } = useGetSchoolsQuery(params);

  const tableData = data?.data?.[0]?.records || [];
  const totalRecords = data?.data?.[0]?.pagination?.[0]?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  return {
    columns,
    onAdd,
    onCancelFilter,
    onFilterBtnClick,
    openFilter,
    filterData,
    setFilterData,
    tableData,
    isPending,
    isError,
    pageNumber,
    pageSize,
    totalPages,
    totalRecords,
    setPageNumber,
    setPageSize,
  };
};

export default useSchool;
